import moment from 'moment';
import { createSelector } from 'reselect';
import { whatIfOrDefaultSelector, whatIfInnerSelector } from '../../WhatIfsSelector';
import { selectCurrentClient } from '../../../CommonSelectors/CommonAreaSelector';
import {
  MAX_RETIREMENT_YEAR,
  MIN_RETIREMENT_YEAR,
} from '../../../ProfileOPL/Income/forms/SocialSecurity/SocialSecurityForm.meta';

export const principalNameSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};
  return whatIfOptions.PrincipalName || '';
};

export const partnerNameSelector = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};
  return whatIfOptions.SpouseName;
};
export const hasPartnerSelector = (state, props) => !!partnerNameSelector(state, props);

export const principalAgeSelector = (state, props) => {
  return whatIfInnerSelector(state, props).SocialSecurityStartAgePrincipal;
};

export const partnerAgeSelector = (state, props) => {
  return whatIfInnerSelector(state, props).SocialSecurityStartAgeSpouse;
};

export const fileForSurvivorBenefitsSelector = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityFileForSurvivorBenefits;
export const useSpousalBenefitSelector = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityUseSpousalBenefit;

export const amountSpouseSelector = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityAmountSpouse;
export const amountPrincipalSelector = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityAmountPrincipal;

export const selectSocialSecurityCurrentlyReceivingBenefitsPrincipal = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityCurrentlyReceivingBenefitsPrincipal;

export const selectSocialSecurityCurrentlyReceivingBenefitsSpouse = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityCurrentlyReceivingBenefitsSpouse;

export const selectSocialSecurityIncomeFrequencyPrincipal = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityIncomeFrequencyPrincipal;

export const selectSocialSecurityIncomeFrequencySpouse = (state, props) =>
  whatIfInnerSelector(state, props).SocialSecurityIncomeFrequencySpouse;

const selectPrincipalCurrentAge = createSelector(selectCurrentClient, ({ Dob: primaryDob }) => {
  return moment().diff(primaryDob, 'years');
});

const selectSpouseCurrentAge = createSelector(selectCurrentClient, ({ PartnerDob: partnerDob }) => {
  return moment().diff(partnerDob, 'years');
});

const selectPrincipalHasMonthsFRA = createSelector(selectCurrentClient, ({ Dob: primaryDob }) => {
  const year = moment(primaryDob).year();

  return year >= MIN_RETIREMENT_YEAR && year <= MAX_RETIREMENT_YEAR;
});

const selectSpouseHasMonthsFRA = createSelector(
  selectCurrentClient,
  ({ PartnerDob: partnerDob }) => {
    const year = moment(partnerDob).year();

    return year >= MIN_RETIREMENT_YEAR && year <= MAX_RETIREMENT_YEAR;
  },
);

export const selectPrincipalStartBenefitsAgeOptions = (state, props) => {
  const principalCurrentAge = selectPrincipalCurrentAge(state);

  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return (whatIfOptions.PrincipalSocialSecurityStartAgeOptions || [])
    .filter(age => age > principalCurrentAge)
    .map(y => ({
      value: y,
      label: y.toString(),
    }));
};

const selectPrincipalFRA = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};
  const { PrincipalFra } = whatIfOptions;

  if (!PrincipalFra) {
    return {};
  }

  return PrincipalFra;
};

export const selectPrincipalAgeAtFillingOptions = (state, props) => {
  const principalCurrentAge = selectPrincipalCurrentAge(state);

  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return (whatIfOptions.PrincipalSocialSecurityStartAgeOptions || [])
    .filter(age => age <= principalCurrentAge)
    .map(y => ({
      value: y,
      label: y.toString(),
    }));
};

export const selectPrincipalAgeOptions = (state, props) => {
  const socialSecurityCurrentlyReceivingBenefits = selectSocialSecurityCurrentlyReceivingBenefitsPrincipal(
    state,
    props,
  );

  const principalAgeAtFillingOptions = selectPrincipalAgeAtFillingOptions(state, props);
  const principalStartBenefitsAgeOptions = selectPrincipalStartBenefitsAgeOptions(state, props);
  const hasMonths = selectPrincipalHasMonthsFRA(state);
  const principalFRA = selectPrincipalFRA(state);

  const options = socialSecurityCurrentlyReceivingBenefits
    ? principalAgeAtFillingOptions
    : principalStartBenefitsAgeOptions;

  const monthsText = principalFRA.Month === 1 ? 'month' : 'months';
  const months = principalFRA.Month > 0 ? `& ${principalFRA.Month} ${monthsText}` : '';

  return options.reduce((acc, option) => {
    if (principalFRA.Year === option.value) {
      if (hasMonths && principalFRA.Month !== 0) {
        return [
          ...acc,
          option,
          {
            value: 'FRA',
            label: `${principalFRA.Year} ${hasMonths ? months : ''} - Full Retirement Age `,
          },
        ];
      }

      return [
        ...acc,
        {
          value: 'FRA',
          label: `${principalFRA.Year} - Full Retirement Age `,
        },
      ];
    }

    return [...acc, option];
  }, []);
};

export const selectPrincipalIsFRA = (state, props) =>
  whatIfInnerSelector(state, props).UseFraPrincipal;

export const selectPrincipalAge = (state, props) => {
  const age = principalAgeSelector(state, props);
  const agesOptions = selectPrincipalAgeOptions(state, props);
  const ages = agesOptions.map(({ value }) => value);

  const isFRA = whatIfInnerSelector(state, props).UseFraPrincipal;

  if (!ages.includes(age) && isFRA) {
    return 'FRA';
  }
  return age;
};

const selectSpouseFRA = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};
  const { SpouseFra } = whatIfOptions;

  if (!SpouseFra) {
    return {};
  }

  return SpouseFra;
};

export const selectSpouseStartBenefitsAgeOptions = (state, props) => {
  const spouseCurrentAge = selectSpouseCurrentAge(state);

  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return (whatIfOptions.SpouseSocialSecurityStartAgeOptions || [])
    .filter(age => age > spouseCurrentAge)
    .map(y => ({
      value: y,
      label: y.toString(),
    }));
};

export const selectSpouseAgeAtFillingOptions = (state, props) => {
  const spouseCurrentAge = selectSpouseCurrentAge(state);

  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return (whatIfOptions.SpouseSocialSecurityStartAgeOptions || [])
    .filter(age => age <= spouseCurrentAge)
    .map(y => ({
      value: y,
      label: y.toString(),
    }));
};

export const selectPrincipalIsCurrentlyReceivingBenefitsUnderCurrentPlan = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return whatIfOptions.PrincipalIsCurrentlyReceivingBenefitsUnderCurrentPlan;
};

export const selectSpouseIsCurrentlyReceivingBenefitsUnderCurrentPlan = (state, props) => {
  const whatIf = whatIfOrDefaultSelector(state, props);
  const whatIfOptions = whatIf.Options || {};

  return whatIfOptions.SpouseIsCurrentlyReceivingBenefitsUnderCurrentPlan;
};

export const selectSpouseAgeOptions = (state, props) => {
  const socialSecurityCurrentlyReceivingBenefits = selectSocialSecurityCurrentlyReceivingBenefitsSpouse(
    state,
    props,
  );

  const spouseAgeAtFillingOptions = selectSpouseAgeAtFillingOptions(state, props);
  const spouseStartBenefitsAgeOptions = selectSpouseStartBenefitsAgeOptions(state, props);
  const hasMonths = selectSpouseHasMonthsFRA(state);
  const spouseFRA = selectSpouseFRA(state);

  const options = socialSecurityCurrentlyReceivingBenefits
    ? spouseAgeAtFillingOptions
    : spouseStartBenefitsAgeOptions;

  const monthsText = spouseFRA.Month === 1 ? 'month' : 'months';
  const months = spouseFRA.Month > 0 ? `& ${spouseFRA.Month} ${monthsText}` : '';

  return options.reduce((acc, option) => {
    if (spouseFRA.Year === option.value) {
      if (hasMonths && spouseFRA.Month !== 0) {
        return [
          ...acc,
          {
            value: 'FRA',
            label: `${spouseFRA.Year} ${hasMonths ? months : ''} - Full Retirement Age `,
          },
        ];
      }
      return [
        ...acc,
        {
          value: 'FRA',
          label: `${spouseFRA.Year} - Full Retirement Age `,
        },
      ];
    }

    return [...acc, option];
  }, []);
};

export const selectSpouseIsFRA = (state, props) => whatIfInnerSelector(state, props).UseFraSpouse;

export const selectSpouseAge = (state, props) => {
  const age = partnerAgeSelector(state, props);
  const agesOptions = selectSpouseAgeOptions(state, props);
  const ages = agesOptions.map(({ value }) => value);

  const isFRA = selectSpouseIsFRA(state, props);

  if (!ages.includes(age) && isFRA) {
    return 'FRA';
  }
  return age;
};
